// src/components/OrderTracker.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import './OrderTracker.css';

const STATUS_STEPS = [
  { key: 'pending',   label: 'Order Placed', icon: '📋' },
  { key: 'confirmed', label: 'Confirmed',    icon: '✅' },
  { key: 'preparing', label: 'Preparing',    icon: '👨‍🍳' },
  { key: 'ready',     label: 'Ready',        icon: '🔔' },
  { key: 'served',    label: 'Served',       icon: '🍽️' },
];

const STATUS_INDEX = {
  pending: 0, confirmed: 1, preparing: 2, ready: 3, served: 4,
};

const STATUS_LABEL = {
  pending:   'Waiting for confirmation...',
  confirmed: 'Order confirmed! ✅',
  preparing: 'Being prepared 👨‍🍳',
  ready:     'Ready for pickup / service 🔔',
  served:    'Served! Enjoy your meal 🍽️',
  cancelled: 'Order cancelled ❌',
};

const STATUS_COLOR = {
  pending:   '#D97706',
  confirmed: '#2563EB',
  preparing: '#7C3AED',
  ready:     '#059669',
  served:    '#6B7280',
  cancelled: '#DC2626',
};

const POLL_INTERVAL = 8000;

// ── Single order view ─────────────────────────────────────────────
const OrderView = ({ order, primaryColor, onSearchAnother, lastUpdated, statusChanged }) => {
  const currentStep = STATUS_INDEX[order.status] ?? 0;
  const isCancelled = order.status === 'cancelled';

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  };

  return (
    <div className={`ot-tracking-section ${statusChanged ? 'ot-status-flash' : ''}`}>

      {/* Meta grid */}
      <div className="ot-meta-card">
        <div className="ot-meta-grid">
          <div className="ot-meta-item">
            <span className="ot-meta-label">Order No.</span>
            <span className="ot-meta-value" style={{ color: primaryColor, fontWeight: 800 }}>
              {order.order_number}
            </span>
          </div>
          <div className="ot-meta-item">
            <span className="ot-meta-label">Customer</span>
            <span className="ot-meta-value">{order.customer_name}</span>
          </div>
          {order.table_number && (
            <div className="ot-meta-item">
              <span className="ot-meta-label">Table</span>
              <span className="ot-meta-value">🪑 {order.table_number}</span>
            </div>
          )}
          <div className="ot-meta-item">
            <span className="ot-meta-label">Type</span>
            <span className="ot-meta-value ot-capitalize">
              {order.order_type?.replace('_', ' ')}
            </span>
          </div>
          <div className="ot-meta-item">
            <span className="ot-meta-label">Placed at</span>
            <span className="ot-meta-value">{formatTime(order.created_at)}</span>
          </div>
          <div className="ot-meta-item">
            <span className="ot-meta-label">Total</span>
            <span className="ot-meta-value" style={{ color: primaryColor, fontWeight: 800 }}>
              ₹{parseFloat(order.total_amount).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Status banner */}
      <div
        className="ot-status-banner"
        style={{
          backgroundColor: (STATUS_COLOR[order.status] || primaryColor) + '15',
          borderColor:     (STATUS_COLOR[order.status] || primaryColor) + '40',
          color:            STATUS_COLOR[order.status] || primaryColor,
        }}
      >
        <span
          className="ot-status-banner-dot"
          style={{ backgroundColor: STATUS_COLOR[order.status] || primaryColor }}
        />
        {STATUS_LABEL[order.status] || order.status}
      </div>

      {/* Steps or cancelled */}
      {!isCancelled ? (
        <div className="ot-steps">
          {STATUS_STEPS.map((step, index) => {
            const isDone    = index < currentStep;
            const isCurrent = index === currentStep;
            return (
              <div key={step.key} className="ot-step">
                <div className="ot-step-col">
                  <div
                    className={`ot-step-circle ${isDone ? 'ot-done' : ''} ${isCurrent ? 'ot-current' : ''}`}
                    style={isCurrent ? {
                      borderColor:     primaryColor,
                      backgroundColor: `${primaryColor}15`,
                      color:           primaryColor,
                      boxShadow:       `0 0 0 4px ${primaryColor}20`,
                    } : isDone ? {
                      backgroundColor: primaryColor,
                      borderColor:     primaryColor,
                    } : {}}
                  >
                    {isDone ? '✓' : step.icon}
                  </div>
                  {index < STATUS_STEPS.length - 1 && (
                    <div
                      className={`ot-step-line ${isDone ? 'ot-line-done' : ''}`}
                      style={isDone ? { backgroundColor: primaryColor } : {}}
                    />
                  )}
                </div>
                <div className="ot-step-labels">
                  <span
                    className={`ot-step-label ${isCurrent ? 'ot-label-current' : ''} ${isDone ? 'ot-label-done' : ''}`}
                    style={isCurrent ? { color: primaryColor } : {}}
                  >
                    {step.label}
                  </span>
                  {isCurrent && (
                    <span className="ot-step-sub" style={{ color: primaryColor }}>● In progress</span>
                  )}
                  {isDone && (
                    <span className="ot-step-sub ot-step-done-text">Done ✓</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="ot-cancelled">
          <div className="ot-cancelled-icon">❌</div>
          <p className="ot-cancelled-title">Order Cancelled</p>
          <p className="ot-cancelled-sub">Please contact staff for assistance.</p>
        </div>
      )}

      {/* Last updated */}
      {lastUpdated && (
        <div className="ot-last-updated">
          <span>🔄</span>
          Updated {lastUpdated.toLocaleTimeString('en-IN', {
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
          })}
        </div>
      )}

      {/* Search another */}
      <button
        className="ot-search-another"
        style={{ color: primaryColor, borderColor: `${primaryColor}40` }}
        onClick={onSearchAnother}
      >
        🔍 Track a different order
      </button>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────
const OrderTracker = ({ activeOrder, tenant, onLookup, onDismiss }) => {
  const primaryColor = tenant?.primary_color || '#FF9F0D';

  const [isMinimized, setIsMinimized] = useState(false);
  const [showPanel,   setShowPanel]   = useState(false);
  const [showLookup,  setShowLookup]  = useState(!activeOrder);

  const [phone,     setPhone]     = useState('');
  const [searching, setSearching] = useState(false);
  const [lookupMsg, setLookupMsg] = useState(null);

  // ── Multi-order state ──────────────────────────────────────────
  const [orders,        setOrders]        = useState([]);
  const [activeTabIdx,  setActiveTabIdx]  = useState(0);
  const [lastUpdated,   setLastUpdated]   = useState(null);
  const [statusChanged, setStatusChanged] = useState(false);

  const pollRef      = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; stopPolling(); };
  }, []);

  const displayOrder = orders[activeTabIdx] || null;
  const hasOrders    = orders.length > 0;

  // ── Stop polling ───────────────────────────────────────────────
  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  // ── Poll ALL order IDs ─────────────────────────────────────────
  const startPolling = useCallback((orderIds) => {
    stopPolling();
    if (!orderIds?.length || !onLookup) return;

    pollRef.current = setInterval(async () => {
      if (!isMountedRef.current) return;
      try {
        const updated = await Promise.all(
          orderIds.map(id => onLookup(null, id))
        );
        const valid = updated.filter(Boolean);
        if (!isMountedRef.current || !valid.length) return;

        setOrders(prev => {
          const changed = valid.some((newO, i) => prev[i]?.status !== newO.status);
          if (changed) {
            setStatusChanged(true);
            setTimeout(() => setStatusChanged(false), 2500);
          }
          return valid;
        });
        setLastUpdated(new Date());

        const allDone = valid.every(o => ['served', 'cancelled'].includes(o.status));
        if (allDone) stopPolling();
      } catch (_) {}
    }, POLL_INTERVAL);
  }, [onLookup]);

  // ── Seed from activeOrder — reads _allActiveOrders if present ──
  useEffect(() => {
    if (!activeOrder?.id) return;

    // Use full sibling list if piggybacked by placeOrder, else just this order
    const seedOrders = activeOrder._allActiveOrders?.length
      ? activeOrder._allActiveOrders
      : [activeOrder];

    // Strip internal field before storing in UI state
    const cleanOrders = seedOrders.map(({ _allActiveOrders, ...o }) => o);

    setOrders(cleanOrders);
    setActiveTabIdx(0);
    setShowLookup(false);
    setShowPanel(true);
    startPolling(cleanOrders.map(o => o.id));
  }, [activeOrder?.id]);

  // ── Drag ──────────────────────────────────────────────────────
  const trackerRef = useRef(null);
  const dragRef    = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const [pos,        setPos]        = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile,   setIsMobile]   = useState(window.innerWidth <= 480);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onDragStart = useCallback((e) => {
    if (isMobile) return;
    if (e.target.closest('button, input, a')) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { dragging: true, startX: clientX, startY: clientY, origX: pos.x, origY: pos.y };
    setIsDragging(true);
  }, [pos, isMobile]);

  const onDragMove = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = clientX - dragRef.current.startX;
    const dy = clientY - dragRef.current.startY;
    const el = trackerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      x: Math.min(Math.max(0, dragRef.current.origX + dx), window.innerWidth  - rect.width),
      y: Math.min(Math.max(0, dragRef.current.origY + dy), window.innerHeight - rect.height),
    });
  }, []);

  const onDragEnd = useCallback(() => {
    dragRef.current.dragging = false;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', onDragMove);
      window.addEventListener('mouseup',   onDragEnd);
      window.addEventListener('touchmove', onDragMove, { passive: false });
      window.addEventListener('touchend',  onDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup',   onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend',  onDragEnd);
    };
  }, [isDragging, onDragMove, onDragEnd]);

  // ── Phone lookup ───────────────────────────────────────────────
  const handleLookup = async () => {
    if (!phone.trim()) return;
    setSearching(true);
    setLookupMsg(null);
    stopPolling();

    const result = await onLookup(phone.trim(), null, true); // multi=true
    if (!isMountedRef.current) return;
    setSearching(false);

    if (result && (Array.isArray(result) ? result.length > 0 : true)) {
      const orderList = Array.isArray(result) ? result : [result];
      setOrders(orderList);
      setActiveTabIdx(0);
      setLookupMsg({
        type: 'found',
        text: `${orderList.length} active order${orderList.length > 1 ? 's' : ''} found!`,
      });
      setShowLookup(false);
      setShowPanel(true);
      setLastUpdated(new Date());
      startPolling(orderList.map(o => o.id));
    } else {
      setLookupMsg({ type: 'not_found', text: 'No active orders found for this number.' });
    }
  };

  const handleDismiss = () => {
    stopPolling();
    setOrders([]);
    setLookupMsg(null);
    setShowPanel(false);
    setShowLookup(false);
    setPhone('');
    if (onDismiss) onDismiss();
  };

  const handleSearchAnother = () => {
    stopPolling();
    setOrders([]);
    setPhone('');
    setLookupMsg(null);
    setShowLookup(true);
  };

  // ── Pill content ───────────────────────────────────────────────
  const pillContent = () => {
    if (!hasOrders) return (
      <>
        <span className="ot-pill-icon">📦</span>
        <span className="ot-pill-label" style={{ color: primaryColor }}>Track Order</span>
      </>
    );
    if (orders.length === 1) return (
      <>
        <span
          className={`ot-live-dot ${statusChanged ? 'ot-dot-pulse' : ''}`}
          style={{ backgroundColor: STATUS_COLOR[orders[0].status] || primaryColor }}
        />
        <div className="ot-pill-info">
          <span className="ot-pill-number" style={{ color: primaryColor }}>
            {orders[0].order_number}
          </span>
          <span className="ot-pill-status">
            {STATUS_LABEL[orders[0].status]}
          </span>
        </div>
      </>
    );
    return (
      <>
        <span
          className={`ot-live-dot ${statusChanged ? 'ot-dot-pulse' : ''}`}
          style={{ backgroundColor: primaryColor }}
        />
        <div className="ot-pill-multi">
          <span className="ot-pill-multi-title" style={{ color: primaryColor }}>
            {orders.length} Active Orders
          </span>
          <span className="ot-pill-multi-sub">Tap to track all</span>
        </div>
      </>
    );
  };

  return (
    <div
      ref={trackerRef}
      className={`ot-wrapper ${isDragging ? 'ot-dragging' : ''}`}
      style={isMobile ? {} : {
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >

      {/* ── Pill ── */}
      <div
        className="ot-pill"
        style={{ borderColor: `${primaryColor}50` }}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
      >
        {!isMobile && (
          <div className="ot-drag-handle" title="Drag to move">
            <span /><span /><span />
          </div>
        )}

        <div
          className="ot-pill-content"
          onClick={() => { if (!isDragging) { setIsMinimized(false); setShowPanel(p => !p); } }}
        >
          {pillContent()}
        </div>

        <div className="ot-pill-actions">
          {hasOrders && (
            <button
              className="ot-icon-btn"
              title={isMinimized ? 'Expand' : 'Minimize'}
              onClick={() => setIsMinimized(p => !p)}
              style={{ color: primaryColor }}
            >
              {isMinimized ? '▲' : '▼'}
            </button>
          )}
          <button className="ot-icon-btn ot-close-btn" title="Close" onClick={handleDismiss}>✕</button>
        </div>
      </div>

      {/* ── Panel ── */}
      {!isMinimized && showPanel && (
        <div className="ot-panel" style={{ borderColor: `${primaryColor}30` }}>

          {/* Header */}
          <div className="ot-panel-header" style={{ borderBottomColor: `${primaryColor}20` }}>
            <div className="ot-panel-title">
              <span style={{ color: primaryColor }}>🧾</span>
              <span>Order Status</span>
              {orders.length > 1 && (
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 500 }}>
                  ({orders.length} orders)
                </span>
              )}
            </div>
            {hasOrders && (
              <div className="ot-poll-badge">
                <span className="ot-poll-dot" style={{ backgroundColor: '#10B981' }} />
                <span className="ot-poll-text">Live</span>
              </div>
            )}
          </div>

          {/* Multi-order tabs */}
          {orders.length > 1 && (
            <div className="ot-order-tabs">
              {orders.map((o, i) => (
                <button
                  key={o.id}
                  className={`ot-order-tab ${activeTabIdx === i ? 'ot-tab-active' : ''}`}
                  style={activeTabIdx === i ? {
                    borderColor:     primaryColor,
                    color:           '#fff',
                    backgroundColor: `${primaryColor}15`,
                  } : {}}
                  onClick={() => setActiveTabIdx(i)}
                >
                  <span className="ot-order-tab-number">{o.order_number}</span>
                  <span
                    className="ot-order-tab-status"
                    style={{
                      backgroundColor: (STATUS_COLOR[o.status] || primaryColor) + '20',
                      color:            STATUS_COLOR[o.status] || primaryColor,
                    }}
                  >
                    {o.status}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Order view */}
          {displayOrder && (
            <OrderView
              order={displayOrder}
              primaryColor={primaryColor}
              onSearchAnother={handleSearchAnother}
              lastUpdated={lastUpdated}
              statusChanged={statusChanged}
            />
          )}

          {/* Lookup */}
          {(!hasOrders || showLookup) && (
            <div className="ot-lookup-section">
              <div className="ot-lookup-icon">📦</div>
              <h4 className="ot-lookup-title">Track Your Order</h4>
              <p className="ot-lookup-sub">Enter your registered phone number</p>

              <div className="ot-lookup-row">
                <input
                  type="tel"
                  value={phone}
                  onChange={e => { setPhone(e.target.value); setLookupMsg(null); }}
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                  placeholder="+91 98765 43210"
                  className="ot-phone-input"
                  disabled={searching}
                />
                <button
                  className="ot-search-btn"
                  style={{
                    background: searching ? '#333' : `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
                    boxShadow:  searching ? 'none' : `0 4px 14px ${primaryColor}40`,
                  }}
                  onClick={handleLookup}
                  disabled={searching || !phone.trim()}
                >
                  {searching ? <span className="ot-spinner" /> : '→'}
                </button>
              </div>

              {lookupMsg && (
                <div className={`ot-lookup-msg ${lookupMsg.type === 'found' ? 'ot-msg-found' : 'ot-msg-not-found'}`}>
                  {lookupMsg.type === 'found' ? '✅' : '🔍'} {lookupMsg.text}
                </div>
              )}

              <p className="ot-lookup-hint">🔒 Only orders placed with this number are shown</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderTracker;
